import { Transition } from "@headlessui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { getDifferenceInYearsAndMonths, unwrapConnection } from "@utils/utils";
import clsx from "clsx";
import React, { useMemo, useState } from "react";

import { Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  ApiChatCareStageChoices,
  ApiChatStatusChoices,
  ApiMediaVideoUploadStatusChoices,
  ChatAttachmentKindEnum,
  ChatCareStage,
  ChatType,
  useCreateChatTagMutation,
  useGetAssignedChatTagsQuery,
  useGetChatMediaQuery,
  useGetChatTagsQuery,
  useUpdateChatMutation,
} from "@api/graphql/types-and-hooks";
import { TagOption, TagSelector } from "@components/TagSelector/TagSelector";
import { convertReadableString } from "@components/forms/utils";
import { notifyError } from "@components/notifications/notifications";
import { ChatHistory } from "@pages/FronteraClarity/components/ChatHistory";
import { ExpertChatNotes } from "@pages/FronteraClarity/components/ExpertChatNotes";
import { ImagePreview, ImagePreviewModal } from "@pages/FronteraClarity/components/ImagePreviewModal";
import { MultimediaCard, MultimediaItem } from "@pages/FronteraClarity/components/MultimediaCard";
import { VideoSessionDetailsPinnedNote } from "@pages/VideoSessions/VideoSessionDetails";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";

// Filter pills map 1:1 to the FDM `ChatAttachmentKindEnum` so the click
// flows straight through to the server-side filter via the
// `getChatMedia` query — no client-side filtering.
const MULTIMEDIA_FILTER_OPTIONS: readonly ChatAttachmentKindEnum[] = [
  ChatAttachmentKindEnum.Any,
  ChatAttachmentKindEnum.Video,
  ChatAttachmentKindEnum.Image,
];

const MULTIMEDIA_FILTER_LABEL: Record<ChatAttachmentKindEnum, string> = {
  [ChatAttachmentKindEnum.Any]: "All",
  [ChatAttachmentKindEnum.Video]: "Videos",
  [ChatAttachmentKindEnum.Image]: "Images",
};

const MULTIMEDIA_FILTER_EMPTY_STATE: Record<ChatAttachmentKindEnum, string> = {
  [ChatAttachmentKindEnum.Any]: "No media shared yet.",
  [ChatAttachmentKindEnum.Video]: "No videos shared yet.",
  [ChatAttachmentKindEnum.Image]: "No images shared yet.",
};

const CARE_STAGE_ORDER: ApiChatCareStageChoices[] = [
  ApiChatCareStageChoices.Unknown,
  ApiChatCareStageChoices.PreDiagnosis,
  ApiChatCareStageChoices.DiagnosticProcess,
  ApiChatCareStageChoices.DiagnosedNotReceivingServices,
  ApiChatCareStageChoices.WaitlistedForServices,
  ApiChatCareStageChoices.ActiveTreatment,
  ApiChatCareStageChoices.EarlyIntervention,
  ApiChatCareStageChoices.Transition,
];

interface ChatDetailSidebarProps {
  chat: ChatType;
  isOpen: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  /** Fires when a card in the Multimedia section is tapped. Includes
   *  the pinned-note payload (the originating chat message) so the
   *  opener can render the "A note from …" card in the video overlay. */
  onMediaClick?: (params: { mediaId: string; pinnedNote?: VideoSessionDetailsPinnedNote }) => void;
}

export const ChatDetailSidebar: React.FC<ChatDetailSidebarProps> = ({ chat, isOpen, setOpen, onMediaClick }) => {
  const queryClient = useQueryClient();
  const { isMobile, isDesktop } = useBreakpoint();

  const [openServiceDetails, setOpenServiceDetails] = useState<boolean>(true);
  const [openChatMedia, setOpenChatMedia] = useState<boolean>(false);
  const [openExpertNotes, setOpenExpertNotes] = useState<boolean>(true);
  const [openChatHistory, setOpenChatHistory] = useState<boolean>(false);

  const childFirstName = chat.learner.learnermetadata?.firstName;
  const childLastName = chat.learner.learnermetadata?.lastName;
  const dateOfBirth = chat.learner.learnermetadata?.birthDate;
  const childAge = dateOfBirth ? getDifferenceInYearsAndMonths(dateOfBirth, new Date()) : "";

  const caregiverFirstName = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.firstName;
  const caregiverLastName = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.lastName;
  const caregiverEmail = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.email;
  const caregiverCity = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.city;
  const caregiverState = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.state;
  const caregiverCareStage = chat.learner.relationshipSet.edges[0]?.node?.caregiver.caregivermetadata?.careStage;

  const expertProvider = chat.members?.edges?.find((edge) => edge?.node?.member?.provider)?.node?.member?.provider;
  const expertFirstName = expertProvider?.providermetadata?.firstName;
  const expertLastName = expertProvider?.providermetadata?.lastName;

  const {
    data: assignedTopics,
    refetch: refetchTopics,
    isLoading: topicsLoading,
  } = useGetAssignedChatTagsQuery({ chatId: chat.id }, { queryKey: ["getAssignedTopics", chat.id] });

  const [previewImage, setPreviewImage] = useState<ImagePreview | null>(null);
  const [multimediaFilter, setMultimediaFilter] = useState<ChatAttachmentKindEnum>(ChatAttachmentKindEnum.Any);

  // Single ANY-keyed query: gets every attachment-bearing message in the
  // chat, server-sorted newest-first. Pill kind-filtering and per-kind
  // counts both fall out of this one response — at the dataset size we
  // operate at (typically <30 attachments per chat) client-side slicing
  // is essentially free and avoids extra roundtrips on pill clicks.
  //
  // Deferred: `enabled: openChatMedia` means the fetch only fires once
  // the user expands the Multimedia accordion. New-message WS events in
  // LiveChat still invalidate ["getChatMedia", chat.id]; when the
  // accordion is open, that invalidation refetches in the background;
  // when closed, the invalidation just marks the cache stale and the
  // next expansion gets fresh data.
  const { data: mediaData, isLoading: mediaLoading } = useGetChatMediaQuery(
    { chatId: chat.id, attachmentKind: ChatAttachmentKindEnum.Any },
    { queryKey: ["getChatMedia", chat.id], enabled: openChatMedia },
  );

  // Flatten ChatMessage edges into MultimediaItem cards. One message
  // can yield 0–2 cards if it carries both media and file.
  const allMultimediaItems = useMemo<MultimediaItem[]>(() => {
    const edges = mediaData?.chatMessages?.edges ?? [];
    return edges.flatMap<MultimediaItem>((edge) => {
      const msg = edge?.node;
      if (!msg) return [];
      const items: MultimediaItem[] = [];
      if (msg.media && msg.media.videoUploadStatus === ApiMediaVideoUploadStatusChoices.Completed) {
        items.push({
          key: `media-${msg.id}`,
          kind: "video",
          mediaId: msg.media.id,
          thumbnailUrl: msg.media.thumbnailUrl ?? null,
          title: `Video from ${[msg.userFirstName, msg.userLastName].filter(Boolean).join(" ") || "caregiver"}`,
          createdDate: msg.createdDate,
          sourceMessage: msg,
        });
      }
      if (msg.file) {
        const isImage = (msg.file.mimeType ?? "").startsWith("image/");
        const imageUrl = msg.file.signedS3LearnerFile ?? null;
        items.push({
          key: `file-${msg.id}`,
          kind: isImage ? "image" : "file",
          mediaId: null,
          thumbnailUrl: isImage ? imageUrl : null,
          imageUrl: isImage ? imageUrl : null,
          title: msg.file.originalFileName ?? "Attachment",
          createdDate: msg.createdDate,
          sourceMessage: msg,
        });
      }
      return items;
    });
  }, [mediaData]);

  // Pill-level filter applied client-side over the (small) attachment
  // list. ANY → show everything; VIDEO/IMAGE → narrow to that kind.
  const visibleMultimediaItems = useMemo<MultimediaItem[]>(() => {
    if (multimediaFilter === ChatAttachmentKindEnum.Any) return allMultimediaItems;
    const targetKind = multimediaFilter === ChatAttachmentKindEnum.Video ? "video" : "image";
    return allMultimediaItems.filter((item) => item.kind === targetKind);
  }, [allMultimediaItems, multimediaFilter]);

  // Counts derived from the same single source so the badges can't drift
  // out of sync with the items list.
  const multimediaCounts = useMemo<Record<ChatAttachmentKindEnum, number>>(
    () =>
      allMultimediaItems.reduce<Record<ChatAttachmentKindEnum, number>>(
        (acc, item) => {
          acc[ChatAttachmentKindEnum.Any]++;
          if (item.kind === "video") acc[ChatAttachmentKindEnum.Video]++;
          else if (item.kind === "image") acc[ChatAttachmentKindEnum.Image]++;
          return acc;
        },
        {
          [ChatAttachmentKindEnum.Any]: 0,
          [ChatAttachmentKindEnum.Video]: 0,
          [ChatAttachmentKindEnum.Image]: 0,
        },
      ),
    [allMultimediaItems],
  );

  const handleMultimediaCardClick = (item: MultimediaItem) => {
    if (item.kind === "video" && item.mediaId && onMediaClick) {
      const src = item.sourceMessage;
      onMediaClick({
        mediaId: item.mediaId,
        pinnedNote: {
          authorFirstName: src.userFirstName ?? "",
          authorLastName: src.userLastName ?? "",
          content: src.content ?? "",
          createdDate: src.createdDate,
        },
      });
      return;
    }
    if (item.kind === "image" && item.imageUrl) {
      setPreviewImage({ url: item.imageUrl, title: item.title });
    }
  };

  const { data: chatTagsData, refetch: tagsRefetch } = useGetChatTagsQuery(
    { filter: {} },
    { queryKey: ["getChatTags"] },
  );

  const chatTagOptions: TagOption[] =
    unwrapConnection(chatTagsData?.chatTags)?.map((tag) => ({
      id: tag.id,
      primary: tag.name,
    })) || [];

  const defaultTagOptions: TagOption[] =
    unwrapConnection(assignedTopics?.assignedChatTags)?.map((tag) => ({
      id: tag.id,
      primary: tag.name,
    })) || [];

  const careStageOptions: TagOption[] = [
    ...CARE_STAGE_ORDER,
    // Any future enum values not yet accounted for appear at the end
    ...Object.values(ApiChatCareStageChoices).filter((v) => !CARE_STAGE_ORDER.includes(v)),
  ].map((value) => ({
    id: value,
    primary: convertReadableString(value),
  }));

  const updateChat = useUpdateChatMutation();
  const createChatTag = useCreateChatTagMutation();

  const updateCareStage = async (careStage: ChatCareStage | null) => {
    try {
      const response = await updateChat.mutateAsync({
        input: {
          chatId: chat.id,
          careStage,
        },
      });

      if (!response.updateChat?.success) {
        notifyError(response.updateChat?.message || "Failed to update care stage.");
        return;
      }

      if (response.updateChat.chat?.status !== ApiChatStatusChoices.Finished) {
        queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["getExpertChatMembers"] });
    } catch (error) {
      console.error("Failed to update care stage: ", error);
    }
  };

  const handleSelectCareStage = (tag: TagOption) => updateCareStage(tag.id as ChatCareStage);
  const handleRemoveCareStage = () => updateCareStage(null);

  const handleSelectTopic = async (tag: TagOption) => {
    if (topicsLoading) return;

    const prevTagIds = unwrapConnection(assignedTopics?.assignedChatTags)?.map((t) => t.id) ?? [];
    const newTagIds = prevTagIds.includes(tag.id) ? prevTagIds : [...prevTagIds, tag.id];

    try {
      const response = await updateChat.mutateAsync({
        input: {
          chatId: chat.id,
          tags: newTagIds,
        },
      });

      if (!response.updateChat?.success) {
        notifyError(response.updateChat?.message || "Failed to update chat topics.");
        return;
      }

      // trigger refetch
      refetchTopics();
    } catch (error) {
      console.error("Failed to update chat topics: ", error);
    }
  };

  const handleRemoveTopic = async (tag: TagOption) => {
    if (topicsLoading) return;

    const prevTagIds = unwrapConnection(assignedTopics?.assignedChatTags)?.map((t) => t.id) ?? [];
    const newTagIds = prevTagIds.filter((id) => id !== tag.id);

    try {
      const response = await updateChat.mutateAsync({
        input: {
          chatId: chat.id,
          tags: newTagIds,
        },
      });

      if (!response.updateChat?.success) {
        notifyError(response.updateChat?.message || "Failed to update chat topics.");
        return;
      }

      // trigger refetch
      refetchTopics();
    } catch (error) {
      console.error("Failed to update chat topics: ", error);
    }
  };

  const handleCreateTopic = async (value: string) => {
    if (!value.trim()) return;

    try {
      const response = await createChatTag.mutateAsync({
        input: {
          chatId: chat.id,
          name: value,
        },
      });

      if (!response.createChatTag?.success) {
        notifyError(response.createChatTag?.message || "Failed to create chat topic.");
        return;
      }

      // trigger refetch of chat and tags list
      refetchTopics();
      tagsRefetch();
    } catch (error) {
      console.error("Failed to create chat topic: ", error);
    }
  };

  return (
    <>
      <Transition
        as="div"
        show={isOpen}
        className={clsx(
          "bg-limestone-50 shrink-0",
          isMobile ? "fixed inset-0 top-[65px]" : "min-w-[400px] max-w-[500px]",
        )}
        enter="transition-transform duration-300 ease-out"
        enterFrom="transform translate-x-full"
        enterTo="transform translate-x-0"
        leave="transition-transform duration-300 ease-in"
        leaveFrom="transform translate-x-0"
        leaveTo="transform translate-x-full"
      >
        <div className={clsx("flex bg-white flex-col border h-full w-full overflow-hidden", isDesktop && "rounded-md")}>
          {!isDesktop && isOpen && setOpen && (
            <div className={clsx("px-4 py-2 bg-white border-b", !isMobile && "rounded-t-md")}>
              <button onClick={() => setOpen(false)}>
                <Paragraph className="text-rio-600!" displayType="loud" size="medium">
                  Back
                </Paragraph>
              </button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Service Details */}
            <div className="flex flex-row px-4 mt-4 justify-between items-center">
              <Paragraph displayType="loud" colorType="primary" size="small">
                Details
              </Paragraph>
              {openServiceDetails ? (
                <ChevronUpIcon
                  className="w-4 h-4 cursor-pointer"
                  onClick={() => {
                    setOpenServiceDetails(false);
                  }}
                />
              ) : (
                <ChevronDownIcon
                  className="w-4 h-4 cursor-pointer"
                  onClick={() => {
                    setOpenServiceDetails(true);
                  }}
                />
              )}
            </div>
            <div className="p-4 border-b">
              <Transition
                as="div"
                show={openServiceDetails}
                className="overflow-hidden"
                enter="transition transition-[max-height] duration-300 ease-in"
                enterFrom="transform max-h-0"
                enterTo="transform max-h-screen"
                leave="transition transition-[max-height] duration-400 ease-out"
                leaveFrom="transform max-h-screen"
                leaveTo="transform max-h-0"
              >
                <table>
                  <tbody>
                    {expertFirstName && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Expert Name</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary">
                            {expertFirstName} {expertLastName}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {childFirstName && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Child Name</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {childFirstName} {childLastName}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {caregiverFirstName && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Parent/Guardian</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {caregiverFirstName} {caregiverLastName}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {caregiverEmail && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Email</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {caregiverEmail}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {childAge && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Child Age</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {childAge}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {dateOfBirth && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Date of Birth</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {dateOfBirth}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {caregiverCity && caregiverState && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Location</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary" className="ph-no-capture">
                            {caregiverCity}, {caregiverState}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    {caregiverCareStage && (
                      <tr>
                        <td className="pr-6 text-wrap py-1">
                          <Paragraph size="small">Onboarding Care Stage</Paragraph>
                        </td>
                        <td className="py-1">
                          <Paragraph size="small" colorType="primary">
                            {convertReadableString(caregiverCareStage)}
                          </Paragraph>
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td className="pr-6 text-wrap py-1 align-top w-[110px]">
                        <Paragraph size="small">Care Stage</Paragraph>
                      </td>
                      <td className="py-1 align-top hover:cursor-pointer">
                        <TagSelector
                          helpText="Select an option"
                          options={careStageOptions}
                          handleSelect={handleSelectCareStage}
                          handleRemove={handleRemoveCareStage}
                          defaultSelectedTags={
                            chat.careStage
                              ? [{ id: chat.careStage, primary: convertReadableString(chat.careStage) }]
                              : []
                          }
                        />
                      </td>
                    </tr>

                    <tr>
                      <td className="pr-6 text-wrap py-1 align-top">
                        <Paragraph size="small">Topics</Paragraph>
                      </td>
                      <td className="py-1 align-top hover:cursor-pointer">
                        <TagSelector
                          helpText="Select an option or create one"
                          options={chatTagOptions}
                          handleSelect={handleSelectTopic}
                          handleRemove={handleRemoveTopic}
                          handleCreate={handleCreateTopic}
                          defaultSelectedTags={defaultTagOptions}
                          multiSelect
                          allowCreate
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Transition>
            </div>

            {/* Multimedia — list of shared videos + files in this chat,
                server-filtered + sorted newest-first. Tapping a video
                card opens the same overlay used elsewhere; image cards
                open the lightbox; non-image files are non-interactive. */}
            <div className="border-b">
              <div className="flex flex-row p-4 items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                  <Paragraph displayType="loud" colorType="primary" size="small">
                    Multimedia
                  </Paragraph>
                  {mediaLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600" />}
                </div>

                {openChatMedia ? (
                  <ChevronUpIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenChatMedia(false);
                    }}
                  />
                ) : (
                  <ChevronDownIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenChatMedia(true);
                    }}
                  />
                )}
              </div>
              <Transition
                as="div"
                show={openChatMedia}
                className="overflow-hidden"
                enter="transition transition-[max-height] duration-300 ease-in"
                enterFrom="transform max-h-0"
                enterTo="transform max-h-screen"
                leave="transition transition-[max-height] duration-400 ease-out"
                leaveFrom="transform max-h-screen"
                leaveTo="transform max-h-0"
              >
                <div className="flex flex-col gap-2 px-4 pb-4">
                  {/* Filter pills */}
                  <div className="flex flex-row gap-2">
                    {MULTIMEDIA_FILTER_OPTIONS.map((filter) => {
                      const isActive = multimediaFilter === filter;
                      return (
                        <button
                          key={filter}
                          type="button"
                          onClick={() => setMultimediaFilter(filter)}
                          className={clsx(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            isActive
                              ? "bg-teal-700 border-teal-700 text-white"
                              : "bg-white border-limestone-300 text-text-primary hover:bg-limestone-50",
                          )}
                        >
                          {MULTIMEDIA_FILTER_LABEL[filter]}
                          <span
                            className={clsx("ml-1.5 text-[10px]", isActive ? "text-white/80" : "text-limestone-500")}
                          >
                            {multimediaCounts[filter]}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-col gap-2 max-h-[40vh] overflow-y-auto">
                    {visibleMultimediaItems.length === 0 && !mediaLoading ? (
                      <Paragraph size="extra-small" colorType="secondary">
                        {MULTIMEDIA_FILTER_EMPTY_STATE[multimediaFilter]}
                      </Paragraph>
                    ) : (
                      visibleMultimediaItems.map((item) => (
                        <MultimediaCard key={item.key} item={item} onClick={() => handleMultimediaCardClick(item)} />
                      ))
                    )}
                  </div>
                </div>
              </Transition>
            </div>

            {/* Expert Notes */}
            <div>
              <div className="flex flex-row px-4 pt-4 items-center justify-between">
                <Paragraph displayType="loud" colorType="primary" size="small">
                  Expert Notes
                </Paragraph>

                {openExpertNotes ? (
                  <ChevronUpIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenExpertNotes(false);
                    }}
                  />
                ) : (
                  <ChevronDownIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenExpertNotes(true);
                    }}
                  />
                )}
              </div>
              <div className="pb-4 px-4 border-b flex-1 min-h-0">
                <Transition
                  as="div"
                  className="overflow-hidden h-full"
                  show={openExpertNotes}
                  enter="transition transition-[max-height] duration-300 ease-in"
                  enterFrom="transform max-h-0"
                  enterTo="transform max-h-full"
                  leave="transition transition-[max-height] duration-400 ease-out"
                  leaveFrom="transform max-h-full"
                  leaveTo="transform max-h-0"
                >
                  <div className="flex flex-col h-full pt-4">
                    <ExpertChatNotes chat={chat} />
                  </div>
                </Transition>
              </div>
            </div>

            {/* History */}
            <div>
              <div className="flex flex-row px-4 pt-4 items-center justify-between">
                <Paragraph displayType="loud" colorType="primary" size="small">
                  History
                </Paragraph>

                {openChatHistory ? (
                  <ChevronUpIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenChatHistory(false);
                    }}
                  />
                ) : (
                  <ChevronDownIcon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => {
                      setOpenChatHistory(true);
                    }}
                  />
                )}
              </div>
              <div className="pb-4 px-4 border-b flex-1 min-h-0">
                <Transition
                  as="div"
                  className="overflow-hidden h-full"
                  show={openChatHistory}
                  enter="transition transition-[max-height] duration-300 ease-in"
                  enterFrom="transform max-h-0"
                  enterTo="transform max-h-full"
                  leave="transition transition-[max-height] duration-400 ease-out"
                  leaveFrom="transform max-h-full"
                  leaveTo="transform max-h-0"
                >
                  <div className="flex flex-col max-h-[50vh] pt-4">
                    <ChatHistory chat={chat} />
                  </div>
                </Transition>
              </div>
            </div>
          </div>
        </div>
      </Transition>

      <ImagePreviewModal preview={previewImage} onClose={() => setPreviewImage(null)} />
    </>
  );
};
